import type {BookingWizardSlideComponent} from "./types";
import {observer} from "mobx-react";
import {useContext} from "react";
import {BookingStateContext} from "../state";
import {
  YStack,
  Separator,
  ExpandableBlock,
  XStack,
  Button,
  Textarea,
  DashedBadge,
} from "@chayka/ui";
import {transformSlots} from "@/utils/format";
import {WEEKDAY_FORMATTER_LONG, MONTH_DAY_FORMATTER, capitalizeFirstLetter} from "@chayka/utils";
import {BOOKING_WIZARD_SLIDES} from "../constants";

export const ConfirmSlide: BookingWizardSlideComponent = observer(({setSlide}) => {
  const {
    subType,
    bookingRoom,
    bookingHours,
    date,
    isParkingBooking,
    parkingSpot,
    parkingHours,
    carNumber,
    carModel,
    equipment,
    comment,
    setComment,
    fullParkingPrice,
    fullEquipmentPrice,
    fullRoomPrice,
    fullPrice,
  } = useContext(BookingStateContext);

  if (!subType) {
    throw new Error("Не выбран тип бронирования");
  }

  if (!bookingRoom) {
    throw new Error("Не выбрана комната");
  }

  const hoursFormatted = transformSlots(bookingHours);
  const parkingHoursFormatted = transformSlots(parkingHours);
  const weekdayAndHoursFormatted = capitalizeFirstLetter(
    `${WEEKDAY_FORMATTER_LONG.format(date)}, ${hoursFormatted}`,
  );
  const dateFormatted = MONTH_DAY_FORMATTER.format(date).replace(".", "");

  return (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-26 no-scrollbar">
      <YStack className="gap-6">
        <DashedBadge className="mx-6">{bookingRoom.name}</DashedBadge>

        <YStack className="gap-2">
          <div className="text-sm">{weekdayAndHoursFormatted}</div>
          <div className="text-5xl uppercase font-light">{dateFormatted}</div>
          {Boolean((isParkingBooking ? bookingHours : parkingHours).length) && (
            <div className="text-sm">
              Авто:{" "}
              <span className="font-bold">
                {carModel} {carNumber}
              </span>
            </div>
          )}
        </YStack>

        <Separator />

        {equipment.length > 0 && (
          <ExpandableBlock
            label={<div className="text-sm font-bold">Оборудование</div>}
            className="flex flex-col gap-2"
          >
            <YStack className="gap-2">
              {equipment.map((eq) => (
                <XStack key={eq.id} className="justify-between items-center gap-4">
                  <XStack className="gap-3 items-center flex-4">
                    <div className="text-sm font-light">{eq.name}</div>
                  </XStack>
                  <div className="text-sm text-muted-foreground flex flex-1 justify-end font-bold">
                    {eq.price} ₽
                  </div>
                </XStack>
              ))}
            </YStack>
          </ExpandableBlock>
        )}

        {!isParkingBooking && parkingSpot && parkingHours.length > 0 && (
          <ExpandableBlock
            label={<div className="text-sm font-bold">Парковка</div>}
            className="flex flex-col gap-2"
          >
            <YStack className="gap-1">
              <XStack className="items-center justify-between">
                <div className="text-sm font-light">{parkingSpot.name}</div>
                <div className="text-sm font-bold">{fullParkingPrice} ₽</div>
              </XStack>
              <div className="text-sm font-light">{parkingHoursFormatted}</div>
            </YStack>
          </ExpandableBlock>
        )}

        <Button
          variant="secondary"
          onClick={() =>
            setSlide(
              isParkingBooking ? BOOKING_WIZARD_SLIDES.PARKING : BOOKING_WIZARD_SLIDES.SUPER_TYPE,
            )
          }
        >
          Изменить
        </Button>

        <Textarea
          rows={2}
          placeholder="Комментарий к бронированию"
          className="min-h-12 text-sm"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />

        <YStack className="gap-2 text-sm">
          <XStack className="items-center justify-between">
            <div className="">
              {isParkingBooking ? "Стоимость парковки:" : "Стоимость аренды помещения:"}
            </div>
            <div className=" font-bold">{fullRoomPrice} ₽</div>
          </XStack>
          {!isParkingBooking && Boolean(fullParkingPrice) && (
            <XStack className="items-center justify-between">
              <div className="">Стоимость парковки:</div>
              <div className=" font-bold">{fullParkingPrice} ₽</div>
            </XStack>
          )}
          {Boolean(fullEquipmentPrice) && (
            <XStack className="items-center justify-between">
              <div className="">Стоимость аренды оборудования:</div>
              <div className=" font-bold">{fullEquipmentPrice} ₽</div>
            </XStack>
          )}
        </YStack>

        <Separator />

        <XStack className="items-baseline justify-between">
          <div className="text-sm font-bold">Итого к оплате:</div>
          <div className="text-2xl font-bold">{fullPrice} ₽</div>
        </XStack>
      </YStack>
    </div>
  );
});
